import React, { useState, useRef } from 'react';
import { useTheme } from '../context/ThemeContext';
import Sidebar from '../components/Sidebar';
import Topbar from '../components/Topbar';
import api from '../api/client';
import './Projects.css';

const COLUMNS = [
  { id:'todo',       label:'To Do',       color:'#6B7280' },
  { id:'inprogress', label:'In Progress',  color:'#3B82F6' },
  { id:'review',     label:'In Review',    color:'#F5A623' },
  { id:'done',       label:'Done',         color:'#22C55E' },
];

const DEFAULT_TEAM_NAMES = ['Yadhu Krishna','Sanjog'];
const PRIORITIES = ['Low','Medium','High','Urgent'];
const PRIORITY_COLOR = { Low:'#6B7280', Medium:'#3B82F6', High:'#F5A623', Urgent:'#EF4444' };

const INIT_TASKS = [];

const today = new Date().toISOString().split('T')[0];
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-IN',{day:'numeric',month:'short'}) : '—';
const isOverdue = (d,s) => d && d < today && s !== 'done';

const PlusIcon  = () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>;
const CloseIcon = () => <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>;
const KanbanIcon= () => <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><rect x="3" y="3" width="5" height="18" rx="1"/><rect x="10" y="3" width="5" height="12" rx="1"/><rect x="17" y="3" width="5" height="15" rx="1"/></svg>;
const ListIcon  = () => <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/><line x1="8" y1="18" x2="21" y2="18"/><circle cx="3" cy="6" r="1.5" fill="currentColor"/><circle cx="3" cy="12" r="1.5" fill="currentColor"/><circle cx="3" cy="18" r="1.5" fill="currentColor"/></svg>;

function TaskCard({ task, onDragStart, onClick }) {
  const over = isOverdue(task.due, task.status);
  return (
    <div className={`pj-card${over?' overdue':''}`} draggable onDragStart={()=>onDragStart(task)} onClick={()=>onClick(task)}>
      <div className="pj-card-priority" style={{background: PRIORITY_COLOR[task.priority]+'22', color: PRIORITY_COLOR[task.priority]}}>{task.priority}</div>
      <div className="pj-card-title">{task.title}</div>
      {task.tags.map(t=><span key={t} className="pj-card-tag">{t}</span>)}
      <div className="pj-card-foot">
        <div className="pj-card-av">{task.assigned[0]}</div>
        <span className={`pj-card-due${over?' over':''}`}>{over?'⚠ ':'📅 '}{fmtDate(task.due)}</span>
      </div>
    </div>
  );
}

function Column({ col, tasks, onDragStart, onDrop, onCardClick }) {
  const [over, setOver] = useState(false);
  return (
    <div className={`pj-col${over?' drag-over':''}`}
      onDragOver={e=>{e.preventDefault();setOver(true)}}
      onDragLeave={()=>setOver(false)}
      onDrop={()=>{setOver(false);onDrop(col.id)}}>
      <div className="pj-col-hd">
        <div className="pj-col-dot" style={{background:col.color}}/>
        <span className="pj-col-label">{col.label}</span>
        <span className="pj-col-count">{tasks.length}</span>
      </div>
      <div className="pj-col-body">
        {tasks.map(t=><TaskCard key={t.id} task={t} onDragStart={onDragStart} onClick={onCardClick}/>)}
        {tasks.length===0 && <div className="pj-empty">Drop here</div>}
      </div>
    </div>
  );
}

function TaskPanel({ task, theme, onClose, onUpdate, teamNames }) {
  const [t, setT] = useState(task);
  const set = (k,v) => setT(p=>({...p,[k]:v}));
  return (
    <div className="pj-panel-wrap">
      <div className="pj-overlay" onClick={onClose}/>
      <div className="pj-panel" data-theme={theme}>
        <div className="pj-panel-hd">
          <span className="pj-panel-title">Edit Task</span>
          <button className="pj-icon-btn" onClick={onClose}><CloseIcon/></button>
        </div>
        <div className="pj-panel-body">
          <div className="pj-panel-sec">
            <label className="pj-lbl">Title</label>
            <input className="pj-inp" value={t.title} onChange={e=>set('title',e.target.value)}/>
          </div>
          <div className="pj-panel-sec">
            <label className="pj-lbl">Description</label>
            <textarea className="pj-inp pj-ta" rows={3} value={t.desc} onChange={e=>set('desc',e.target.value)}/>
          </div>
          <div className="pj-form-row">
            <div className="pj-panel-sec">
              <label className="pj-lbl">Status</label>
              <select className="pj-inp pj-sel" value={t.status} onChange={e=>set('status',e.target.value)}>
                {COLUMNS.map(c=><option key={c.id} value={c.id}>{c.label}</option>)}
              </select>
            </div>
            <div className="pj-panel-sec">
              <label className="pj-lbl">Priority</label>
              <select className="pj-inp pj-sel" value={t.priority} onChange={e=>set('priority',e.target.value)}>
                {PRIORITIES.map(p=><option key={p}>{p}</option>)}
              </select>
            </div>
          </div>
          <div className="pj-form-row">
            <div className="pj-panel-sec">
              <label className="pj-lbl">Assigned to</label>
              <select className="pj-inp pj-sel" value={t.assigned} onChange={e=>set('assigned',e.target.value)}>
                {[...new Set([t.assigned, ...(teamNames||DEFAULT_TEAM_NAMES)].filter(Boolean))].map(m=><option key={m}>{m}</option>)}
              </select>
            </div>
            <div className="pj-panel-sec">
              <label className="pj-lbl">Due date</label>
              <input className="pj-inp" type="date" value={t.due} onChange={e=>set('due',e.target.value)}/>
            </div>
          </div>
        </div>
        <div className="pj-panel-ft">
          <button className="pj-btn-pri" style={{width:'100%'}} onClick={()=>{onUpdate(t);onClose();}}>Save changes</button>
        </div>
      </div>
    </div>
  );
}

function AddModal({ theme, onClose, onAdd, teamNames }) {
  const [form, setForm] = useState({ title:'', desc:'', status:'todo', priority:'Medium', assigned:'Lakshmi', due:'', tags:[] });
  const set = (k,v) => setForm(f=>({...f,[k]:v}));
  return (
    <div className="pj-modal-overlay" onClick={onClose}>
      <div className="pj-modal" data-theme={theme} onClick={e=>e.stopPropagation()}>
        <div className="pj-modal-hd">
          <span className="pj-modal-title">Add Task</span>
          <button className="pj-icon-btn" onClick={onClose}><CloseIcon/></button>
        </div>
        <div className="pj-modal-body">
          <div className="pj-panel-sec"><label className="pj-lbl">Title *</label><input className="pj-inp" placeholder="Task title" value={form.title} onChange={e=>set('title',e.target.value)}/></div>
          <div className="pj-panel-sec"><label className="pj-lbl">Description</label><textarea className="pj-inp pj-ta" rows={2} placeholder="Details…" value={form.desc} onChange={e=>set('desc',e.target.value)}/></div>
          <div className="pj-form-row">
            <div className="pj-panel-sec"><label className="pj-lbl">Status</label><select className="pj-inp pj-sel" value={form.status} onChange={e=>set('status',e.target.value)}>{COLUMNS.map(c=><option key={c.id} value={c.id}>{c.label}</option>)}</select></div>
            <div className="pj-panel-sec"><label className="pj-lbl">Priority</label><select className="pj-inp pj-sel" value={form.priority} onChange={e=>set('priority',e.target.value)}>{PRIORITIES.map(p=><option key={p}>{p}</option>)}</select></div>
          </div>
          <div className="pj-form-row">
            <div className="pj-panel-sec"><label className="pj-lbl">Assign to</label><select className="pj-inp pj-sel" value={form.assigned} onChange={e=>set('assigned',e.target.value)}>{[...new Set([form.assigned, ...(teamNames||DEFAULT_TEAM_NAMES)].filter(Boolean))].map(m=><option key={m}>{m}</option>)}</select></div>
            <div className="pj-panel-sec"><label className="pj-lbl">Due date</label><input className="pj-inp" type="date" value={form.due} onChange={e=>set('due',e.target.value)}/></div>
          </div>
        </div>
        <div className="pj-modal-ft">
          <button className="pj-btn-sec" onClick={onClose}>Cancel</button>
          <button className="pj-btn-pri" onClick={()=>{if(form.title.trim()){onAdd({...form,id:Date.now(),tags:[]});onClose();}}}>Add Task</button>
        </div>
      </div>
    </div>
  );
}

export default function Projects() {
  const { theme } = useTheme();
  const [tasks, setTasks] = useState([]);
  const [teamNames, setTeamNames] = useState(DEFAULT_TEAM_NAMES);

  React.useEffect(() => {
    api.projects.list()
      .then(rows => setTasks(rows.map(p => ({ ...p, assigned: p.assigned_name, due: p.due_date, desc: p.description }))))
      .catch(err => console.error('Failed to load projects:', err));
    api.team.list()
      .then(team => {
        const names = team.map(m => (m.name || '').trim()).filter(Boolean);
        setTeamNames(names.length ? [...new Set([...names, ...DEFAULT_TEAM_NAMES])] : DEFAULT_TEAM_NAMES);
      })
      .catch(() => {});
  }, []);

  const [view, setView]       = useState('kanban');
  const [selected, setSelected] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const dragging = useRef(null);

  const done = tasks.filter(t=>t.status==='done').length;
  const total = tasks.length;
  const overdue = tasks.filter(t=>isOverdue(t.due,t.status)).length;

  const handleDrop = (colId) => {
    if (!dragging.current) return;
    const taskId = dragging.current.id;
    setTasks(ts => ts.map(t => t.id===taskId ? {...t,status:colId} : t));
    // Backend handles 30-day follow-up creation + notification when status becomes 'done'
    api.projects.update(taskId, { status: colId }).catch(err => console.error('Failed to update project status:', err));
    dragging.current = null;
  };
  const handleUpdate = (updated) => {
    setTasks(ts => ts.map(t => t.id===updated.id ? updated : t));
    api.projects.update(updated.id, updated).catch(err => console.error('Failed to update project:', err));
  };
  const handleAdd = (task) => {
    api.projects.add(task)
      .then(created => setTasks(ts => [created, ...ts.filter(t=>t.id!==task.id)]))
      .catch(err => console.error('Failed to create project:', err));
    setTasks(ts => [task,...ts]);
  };

  const TopActions = (
    <div className="pj-top-acts">
      <div className="pj-view-toggle">
        <button className={`pj-vt-btn${view==='kanban'?' active':''}`} onClick={()=>setView('kanban')}><KanbanIcon/></button>
        <button className={`pj-vt-btn${view==='list'?' active':''}`}   onClick={()=>setView('list')}><ListIcon/></button>
      </div>
      <button className="pj-add-btn" onClick={()=>setShowAdd(true)}><PlusIcon/> Add Task</button>
    </div>
  );

  return (
    <div className="pj-wrap" data-theme={theme}>
      <Sidebar/>
      <div className="pj-main">
        <Topbar title="Projects" subtitle={`${done}/${total} done${overdue?` · ⚠ ${overdue} overdue`:''}`} actions={TopActions}/>

        <div className="pj-summary">
          {COLUMNS.map(col=>{
            const count = tasks.filter(t=>t.status===col.id).length;
            return (
              <div key={col.id} className="pj-sum-item">
                <div className="pj-sum-dot" style={{background:col.color}}/>
                <span className="pj-sum-label">{col.label}</span>
                <span className="pj-sum-count">{count}</span>
              </div>
            );
          })}
        </div>

        {view==='kanban' && (
          <div className="pj-board">
            {COLUMNS.map(col=>(
              <Column key={col.id} col={col}
                tasks={tasks.filter(t=>t.status===col.id)}
                onDragStart={t=>{dragging.current=t}}
                onDrop={handleDrop}
                onCardClick={setSelected}/>
            ))}
          </div>
        )}

        {view==='list' && (
          <div className="pj-list-wrap">
            <table className="pj-table">
              <thead><tr>{['Task','Priority','Status','Assigned','Due'].map(h=><th key={h} className="pj-th">{h}</th>)}</tr></thead>
              <tbody>
                {tasks.map(t=>(
                  <tr key={t.id} className="pj-tr" onClick={()=>setSelected(t)}>
                    <td className="pj-td"><div className="pj-tr-title">{t.title}</div><div className="pj-tr-desc">{t.desc}</div></td>
                    <td className="pj-td"><span className="pj-pri-pill" style={{background:PRIORITY_COLOR[t.priority]+'20',color:PRIORITY_COLOR[t.priority]}}>{t.priority}</span></td>
                    <td className="pj-td"><span className="pj-status-pill" style={{background:COLUMNS.find(c=>c.id===t.status)?.color+'20',color:COLUMNS.find(c=>c.id===t.status)?.color}}>{COLUMNS.find(c=>c.id===t.status)?.label}</span></td>
                    <td className="pj-td pj-td-muted">{t.assigned}</td>
                    <td className={`pj-td${isOverdue(t.due,t.status)?' pj-td-over':' pj-td-muted'}`}>{fmtDate(t.due)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      {selected && <TaskPanel task={selected} theme={theme} onClose={()=>setSelected(null)} onUpdate={handleUpdate} teamNames={teamNames}/>}
      {showAdd   && <AddModal theme={theme} onClose={()=>setShowAdd(false)} onAdd={handleAdd} teamNames={teamNames}/>}
    </div>
  );
}
